import React, { useContext, useMemo } from 'react';
import { useData } from 'resift';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useHistory } from 'react-router';
import makeValidateMappings from 'fetches/makeValidateMappings';
import makeStyles from '@material-ui/core/styles/makeStyles';
import withSource from 'components/withSource/withSource';
import MappingsContext from 'helpers/MappingsContext';
import useIsInWizard from 'helpers/useIsInWizard';
import Directory from 'types/Directory';
import { SourceType } from 'types/Source';
import MappingProps, * as MappingTypes from 'types/Mapping';
import useExistingOrgMappingsInfo from './useExistingOrgMappingsInfo';
import { HOME_URL, DIRECTORIES_URL, SOURCE_URL } from 'components/Router/consts';

import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Accordion from '@material-ui/core/Accordion';
import Button from '@material-ui/core/Button';
import TableBody from '@material-ui/core/TableBody';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import Table from 'components/Table';
import TableHead from 'components/Table/TableHead';
import TableHeadCell from 'components/Table/TableHeadCell';
import OrgSourceMapping from './OrgSourceMapping';
import ExampleOrgConnect from 'components/Logos/ExampleOrgConnect';
import OverallValidationStatus from 'components/Mappings/OverallValidationStatus';
import makeGetMappingsAttributes from 'fetches/makeGetMappingsAttributes';
import getSourceHeaderCopy from 'components/Mappings/_helpers/getSourceHeaderCopy';
import AccordionSectionHeader from 'molecules/AccordionSectionHeader';
import AccordionSectionContent from 'molecules/AccordionSectionContent';
import AccordionSectionButtonContainer from 'molecules/AccordionSectionButtonContainer';
import NoManagerInfoCopy from './NoManagerInfoCopy';

import {
  ORG_TEAM_LEADER_MAPPING_MATCH_KEY,
  ORG_TEAM_LEADER_MAPPING_REF_KEY,
  TL_MATCH_MAPPING_INDEX,
  TL_REF_MAPPING_INDEX,
  MAP_ATTRIBUTES_URL,
  ORG_MAPPINGS_NOT_SET,
  NO_VALID_ORG_REFERENCE_OPTIONS,
} from 'pages/Directory/consts';

interface Props {
  directory: Directory;
  mappings: MappingProps[];
  isExpanded: boolean;
  onUpdateMapping: (newMappingIndex: number, newMapping: MappingProps | MappingProps[]) => void;
  onNextStep: () => void;
  onToggleExpansion: () => void;
}

function OrgMapping({
  directory,
  mappings,
  isExpanded,
  onUpdateMapping,
  onNextStep,
  onToggleExpansion,
}: Props) {
  const classes = useStyles();
  const history = useHistory();
  const mappingsContext: MappingTypes.MappingContext = useContext(MappingsContext);
  const { source, sourceAttrOptions } = mappingsContext;
  const [tlMatchMapping, tlRefMapping] = mappings;
  const isCsvSource = source.type === SourceType.CSV;
  const getMappingsAttributes = makeGetMappingsAttributes(source.id);
  const mappingAttributes = useData(getMappingsAttributes);
  const isInWizard = useIsInWizard();
  const tlRefOptionsForCsv: Array<{
    sourcePath: string;
    tlMatchOptions: Array<{
      sourcePath: string;
    }>;
  }> = _get(mappingAttributes, 'tlRefOptions', []);
  // (note - pearl) When a csv file upload has been skipped, tlRefOptions would be undefined and
  // we should use the suggested sourceAttrOptions like other mapping rows
  const csvFileUploadSkipped = isCsvSource && _get(mappingAttributes, 'tlRefOptions') === undefined;
  const tlRefOptionStrings = csvFileUploadSkipped
    ? sourceAttrOptions
    : tlRefOptionsForCsv.map((option: any) => option.sourcePath);
  const tlRefOptionsEmptyForCsv =
    isCsvSource && !csvFileUploadSkipped && _isEmpty(tlRefOptionStrings);

  const selectedTlRefOption =
    !_isEmpty(tlRefOptionsForCsv) &&
    tlRefOptionsForCsv.find((item: any) => item.sourcePath === tlRefMapping.source);

  const tlMatchOptionsForCsv = _isEmpty(tlRefOptionsForCsv)
    ? sourceAttrOptions
    : !selectedTlRefOption
    ? []
    : selectedTlRefOption.tlMatchOptions.map((option: any) => option.sourcePath);

  const showTlMatchRow =
    (tlMatchOptionsForCsv && tlMatchOptionsForCsv.length > 1) ||
    !isCsvSource ||
    !tlRefOptionStrings.includes(_get(tlRefMapping, 'source'));

  const validateMappings = makeValidateMappings(source.id);
  const validationResponse = useData(validateMappings);
  const validationResults = _get(validationResponse, 'validationResults');

  const existingMappingsInfo = useExistingOrgMappingsInfo(directory, source.id);
  const onSourceCreationWizardMappings = history.location.pathname.includes(MAP_ATTRIBUTES_URL);
  const { orgMappingsSet, orgMappedToSourceId, orgMappedToSourceName } = existingMappingsInfo;

  const ValidationStatus = useMemo(() => {
    const otherOrgMappingIssues = !_isEmpty(validationResults)
      ? validationResults.filter((vr: MappingTypes.MappingValidationResult) =>
          [ORG_MAPPINGS_NOT_SET, NO_VALID_ORG_REFERENCE_OPTIONS].includes(vr.code),
        )
      : undefined;
    return (
      <OverallValidationStatus
        mappings={mappings}
        errorsNotTiedToSpecificMappingRows={otherOrgMappingIssues}
      />
    );
  }, [mappings]);

  const renderCardHeader = () => {
    return (
      <>
        <CardHeader
          title={`${
            onSourceCreationWizardMappings ? 'Step 2: Set Up The Org Chart' : 'Set Up The Org Chart'
          }`}
          classes={{
            subheader: classes.subheaderCardHeader,
            action: classes.action,
          }}
          className={classes.cardHeader}
          action={
            !orgMappingsSet && <div className={classes.validationIcon}>{ValidationStatus}</div>
          }
        />
      </>
    );
  };

  const renderDetails = () => {
    return (
      <>
        <CardContent className={classes.cardContent}>
          {orgMappingsSet && (
            <div className={classes.subtitle}>
              Your org chart data has already been mapped in source:{' '}
              <Link
                href={`${HOME_URL}${DIRECTORIES_URL}/${directory.id}${SOURCE_URL}/${orgMappedToSourceId}`}
                color="secondary"
              >
                {orgMappedToSourceName}
              </Link>
              {'.'}
            </div>
          )}
          {!orgMappingsSet && (
            <>
              {isCsvSource ? (
                <Typography variant="body2" className={classes.description}>
                  To create your org chart, we need to know which column in your CSV file references
                  a person’s <strong>direct manager</strong>. Please choose the column we should use
                  from the list of valid columns below.
                </Typography>
              ) : (
                <Typography className={classes.description} variant="body2">
                  To properly create your Org Chart, we need to understand how a person’s direct
                  manager is represented in your source data.{' '}
                  <strong>
                    You should never need to change this from the default unless your organization
                    represents managers in an alternate way within your data source.
                  </strong>
                  <br />
                  <br />
                  In the <strong>manager property</strong> input, select the property that points to
                  a person’s manager. In the <strong>Employee Property</strong> dropdown, select the
                  property that the manager property is referencing on the associated person.
                </Typography>
              )}
              <Table>
                <TableHead>
                  <TableHeadCell>
                    {source.type ? getSourceHeaderCopy(source.type) : 'Other Source Property'}
                  </TableHeadCell>
                  <TableHeadCell>Preview Data</TableHeadCell>
                  <TableHeadCell colSpan={2} />
                </TableHead>
                <TableBody>
                  <OrgSourceMapping
                    mappingIndex={TL_REF_MAPPING_INDEX}
                    source={source}
                    destination={ORG_TEAM_LEADER_MAPPING_REF_KEY}
                    mapping={tlRefMapping}
                    onUpdateMapping={onUpdateMapping}
                    disabled={orgMappingsSet || (isInWizard && tlRefOptionsEmptyForCsv)}
                    options={isCsvSource ? tlRefOptionStrings : sourceAttrOptions}
                    existingTlMatchMapping={tlMatchMapping}
                  />

                  {isCsvSource &&
                    showTlMatchRow &&
                    (tlRefMapping.source !== '' || !tlRefOptionStrings) && (
                      <div className={classes.orgMappingHelper}>
                        <div className={classes.orgMappingHelperHeading}>
                          <InfoOutlinedIcon className={classes.infoIcon} />
                          <Typography variant="body2">
                            For the above manager header, we weren’t able to correctly identify
                            another column in your file that the manager header references. In the
                            below dropdown, please choose the <strong>employee header</strong> that
                            the manager header is referencing.
                          </Typography>
                        </div>
                        <div>
                          <ExampleOrgConnect />
                        </div>
                      </div>
                    )}

                  {showTlMatchRow && (tlRefMapping.source !== '' || !tlRefOptionStrings) && (
                    <OrgSourceMapping
                      mappingIndex={TL_MATCH_MAPPING_INDEX}
                      source={source}
                      destination={ORG_TEAM_LEADER_MAPPING_MATCH_KEY}
                      mapping={tlMatchMapping}
                      onUpdateMapping={onUpdateMapping}
                      disabled={orgMappingsSet}
                      options={isCsvSource ? tlMatchOptionsForCsv : sourceAttrOptions}
                    />
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </>
    );
  };

  return (
    <>
      {!onSourceCreationWizardMappings || orgMappingsSet ? (
        <>
          {renderCardHeader()}
          {renderDetails()}
          {tlRefOptionsEmptyForCsv && isInWizard && !orgMappingsSet && <NoManagerInfoCopy />}
        </>
      ) : (
        <Accordion expanded={isExpanded} onChange={onToggleExpansion} className={classes.accordion}>
          <AccordionSectionHeader>{renderCardHeader()}</AccordionSectionHeader>
          <AccordionSectionContent>
            {renderDetails()}
            {tlRefOptionsEmptyForCsv && <NoManagerInfoCopy />}
            <AccordionSectionButtonContainer>
              <Button variant="contained" color="secondary" onClick={onNextStep}>
                Next Step
              </Button>
            </AccordionSectionButtonContainer>
          </AccordionSectionContent>
        </Accordion>
      )}
    </>
  );
}

const useStyles = makeStyles(theme => ({
  root: {},
  cardHeader: {
    width: '100%',
  },
  subheaderCardHeader: {
    maxWidth: 1200,
  },
  description: {
    marginBottom: theme.spacing(),
  },
  subtitle: {
    marginTop: theme.spacing(2.125),
    fontSize: theme.typography.body1.fontSize,
    color: theme.palette.common.black,
  },
  orgMapCell: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  success: {
    color: theme.palette.success.main,
  },
  validationIcon: {
    display: 'flex',
    alignItems: 'center',
  },
  orgMappingHelper: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 933,
    marginTop: theme.spacing(2.25),
  },
  orgMappingHelperHeading: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    maxHeight: 18,
    marginLeft: theme.spacing(),
    marginBottom: theme.spacing(2),
  },
  infoIcon: {
    maxHeight: 20,
    maxWidth: 20,
    marginRight: theme.spacing(1.5),
    fill: theme.palette.grey[400],
  },
  accordion: {
    padding: 0,
    margin: 0,
  },
  cardContent: {
    paddingTop: 0,
  },
  action: {
    margin: 0,
    alignSelf: 'center',
  },
}));

export default withSource(OrgMapping);
